// @flow

import React, { PureComponent } from 'react';
import Dropzone from 'react-dropzone';
import styled from 'styled-components';
import { graphql, QueryRenderer } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { colors, fonts } from 'shared/styleguide';
import * as Actions from 'main-app/store/Actions';
import { i18n } from 'shared/utils';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import Page from 'main-app/components/Page';
import UploadCustomerPO from 'main-app/mutations/UploadCustomerPO';
import ConfirmPOExtraction from 'main-app/mutations/ConfirmPOExtraction';
import type { RouterHistory } from 'react-router';
import POReviewForm from './POReviewForm';

type Props = {
  history: RouterHistory,
};

type State = {
  uploading: boolean,
  activeUpload: ?Object,
  polling: boolean,
};

/* ==================== Styled ==================== */

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 0;
`;

const Title = styled.h1`
  ${fonts.headerBold};
  font-size: 22px;
  color: ${colors.charcoalGrey};
  margin: 0;
`;

const BackLink = styled.span`
  ${fonts.bodyRegular};
  color: ${colors.azul};
  cursor: pointer;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

const UploadArea = styled.div`
  border: 2px dashed ${colors.lightBlueGrey};
  border-radius: 12px;
  padding: 60px 40px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: ${colors.white};

  &:hover {
    border-color: ${colors.azul};
    background: rgba(0, 122, 255, 0.03);
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: ${colors.azul};
`;

const UploadTitle = styled.div`
  ${fonts.headerBold};
  color: ${colors.charcoalGrey};
  margin-bottom: 8px;
`;

const UploadSubtitle = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.coolGrey};
  font-size: 13px;
`;

const StatusCard = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 12px;
  padding: 32px;
  text-align: center;
`;

const StatusTitle = styled.div`
  ${fonts.headerBold};
  color: ${colors.charcoalGrey};
  margin-bottom: 8px;
`;

const StatusSubtitle = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.coolGrey};
  font-size: 13px;
`;

const ErrorCard = styled(StatusCard)`
  border-color: ${colors.cherry};
`;

const ErrorText = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.cherry};
  margin-top: 8px;
  font-size: 13px;
`;

const UploadsList = styled.div`
  margin-top: 32px;
`;

const SectionTitle = styled.h2`
  ${fonts.headerBold};
  font-size: 16px;
  color: ${colors.charcoalGrey};
  margin: 0 0 16px 0;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${colors.white};
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: ${(p) => (p.clickable ? 'pointer' : 'default')};
  transition: background 0.15s;

  ${(p) =>
    p.clickable &&
    `&:hover {
      background: ${colors.paleGrey};
    }`}
`;

const UploadFileName = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.charcoalGrey};
  font-size: 14px;
`;

const StatusBadge = styled.span`
  ${fonts.smallBold};
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${(p) => {
    switch (p.status) {
      case 'PROCESSING':
        return `background: #FFF3CD; color: #856404;`;
      case 'READY_FOR_REVIEW':
        return `background: #D4EDDA; color: #155724;`;
      case 'CONFIRMED':
        return `background: #D1ECF1; color: #0C5460;`;
      case 'FAILED':
        return `background: #F8D7DA; color: #721C24;`;
      default:
        return `background: ${colors.paleGrey}; color: ${colors.coolGrey};`;
    }
  }}
`;

const UPLOADS_QUERY = graphql`
  query POUploadSceneUploadsQuery {
    poUploads {
      poUploadId
      fileName
      fileUrl
      status
      errorMessage
      extractedData
      orderId
      createdAt
    }
  }
`;

const UPLOAD_DETAIL_QUERY = graphql`
  query POUploadSceneDetailQuery($poUploadId: ID!) {
    poUpload(poUploadId: $poUploadId) {
      poUploadId
      fileName
      fileUrl
      status
      errorMessage
      extractedData
      matchedData
      orderId
      createdAt
    }
  }
`;

/* ==================== Component ==================== */

class POUploadScene extends PureComponent<Props, State> {
  state = {
    uploading: false,
    activeUpload: null,
    polling: false,
  };

  pollingInterval: any = null;

  componentWillUnmount() {
    this.stopPolling();
  }

  handleDrop = async (accepted: Array<Object>, rejected: Array<Object>) => {
    if (rejected.length) {
      Actions.alertNotification(
        i18n.t('Only PDF files under 50 MB are supported.'),
        'error',
      );
      return;
    }

    const file = accepted[0];
    if (!file) return;

    const uploadables = {};
    uploadables[file.name] = file;

    this.setState({ uploading: true });

    try {
      const result = await UploadCustomerPO.commit({
        variables: { input: {} },
        uploadables,
      });

      const poUpload = result.uploadCustomerPO.poUpload;

      Actions.alertNotification(
        i18n.t('PO uploaded. Extracting data with AI...'),
        'success',
      );

      this.setState({ uploading: false, activeUpload: poUpload });
      this.startPolling(poUpload.poUploadId);
    } catch (e) {
      Actions.alertNotification(e.message || 'Upload failed', 'error');
      this.setState({ uploading: false });
    }
  };

  startPolling = (poUploadId: string) => {
    this.stopPolling();
    this.setState({ polling: true });

    this.pollingInterval = setInterval(async () => {
      try {
        const res = await fetch('/graphql', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: `query POUploadPoll($poUploadId: ID!) {
              poUpload(poUploadId: $poUploadId) {
                poUploadId fileName fileUrl status errorMessage extractedData matchedData orderId createdAt
              }
            }`,
            variables: { poUploadId },
          }),
        });
        const json = await res.json();
        const upload = json.data && json.data.poUpload;

        if (upload && upload.status !== 'PROCESSING') {
          this.stopPolling();
          if (typeof upload.extractedData === 'string') {
            upload.extractedData = JSON.parse(upload.extractedData);
          }
          this.setState({ activeUpload: upload });
        }
      } catch (err) {
        // Continue polling
      }
    }, 3000);
  };

  stopPolling = () => {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.setState({ polling: false });
  };

  handleConfirm = async (matchedData: Object) => {
    const { activeUpload } = this.state;
    if (!activeUpload) return;

    try {
      const result = await ConfirmPOExtraction.commit({
        variables: {
          input: {
            poUploadId: activeUpload.poUploadId,
            matchedData,
          },
        },
      });

      Actions.alertNotification(
        i18n.t('Sales Order created successfully!'),
        'success',
      );

      const orderId = result.confirmPOExtraction.poUpload.orderId;
      if (orderId) {
        this.props.history.push(`/order/${orderId}`);
      } else {
        this.setState({ activeUpload: null });
      }
    } catch (e) {
      Actions.alertNotification(e.message || 'Failed to create order', 'error');
    }
  };

  handleReviewExisting = (upload: Object) => {
    if (upload.status === 'READY_FOR_REVIEW') {
      this.setState({ activeUpload: upload });
    } else if (upload.status === 'CONFIRMED' && upload.orderId) {
      this.props.history.push(`/order/${upload.orderId}`);
    }
  };

  renderUploadArea() {
    const { uploading } = this.state;

    if (uploading) {
      return (
        <StatusCard>
          <Loader />
          <StatusTitle>{i18n.t('Uploading PDF...')}</StatusTitle>
        </StatusCard>
      );
    }

    return (
      <Dropzone
        onDrop={this.handleDrop}
        accept="application/pdf"
        maxSize={50 * 1000 * 1000}
        multiple={false}
      >
        {({ getRootProps, getInputProps }) => (
          <UploadArea {...getRootProps()}>
            <input {...getInputProps()} />
            <UploadIcon>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="12" y2="12" />
                <line x1="15" y1="15" x2="12" y2="12" />
              </svg>
            </UploadIcon>
            <UploadTitle>{i18n.t('Drop a Customer PO here')}</UploadTitle>
            <UploadSubtitle>
              {i18n.t('or click to browse. PDF files only, up to 50 MB.')}
            </UploadSubtitle>
          </UploadArea>
        )}
      </Dropzone>
    );
  }

  renderProcessing() {
    const { activeUpload } = this.state;

    return (
      <StatusCard>
        <Loader />
        <StatusTitle>{i18n.t('Extracting data from PO...')}</StatusTitle>
        <StatusSubtitle>
          {i18n.t('AI is reading "{{fileName}}". This usually takes 10-30 seconds.', {
            fileName: activeUpload?.fileName || 'your file',
          })}
        </StatusSubtitle>
      </StatusCard>
    );
  }

  renderFailed() {
    const { activeUpload } = this.state;

    return (
      <ErrorCard>
        <StatusTitle>{i18n.t('Extraction Failed')}</StatusTitle>
        <ErrorText>{activeUpload?.errorMessage || 'Unknown error'}</ErrorText>
        <div style={{ marginTop: 16 }}>
          <Button theme="blue" onClick={() => this.setState({ activeUpload: null })}>
            {i18n.t('Try Again')}
          </Button>
        </div>
      </ErrorCard>
    );
  }

  renderActiveUpload() {
    const { activeUpload, polling } = this.state;
    if (!activeUpload) return null;

    if (activeUpload.status === 'PROCESSING' || polling) {
      return this.renderProcessing();
    }

    if (activeUpload.status === 'FAILED') {
      return this.renderFailed();
    }

    if (activeUpload.status === 'READY_FOR_REVIEW') {
      return (
        <POReviewForm
          extractedData={activeUpload.extractedData}
          fileName={activeUpload.fileName}
          fileUrl={activeUpload.fileUrl}
          onConfirm={this.handleConfirm}
          onCancel={() => this.setState({ activeUpload: null })}
        />
      );
    }

    return null;
  }

  renderPreviousUploads() {
    return (
      <QueryRenderer
        environment={relayEnvironment}
        query={UPLOADS_QUERY}
        variables={{}}
        cacheConfig={{ force: true }}
        render={({ props, error }) => {
          if (error) return null;
          if (!props) return <Loader />;

          const uploads = props.poUploads || [];
          if (uploads.length === 0) return null;

          return (
            <UploadsList>
              <SectionTitle>{i18n.t('Previous Uploads')}</SectionTitle>
              {uploads.map((u) => (
                <UploadRow
                  key={u.poUploadId}
                  clickable={u.status === 'READY_FOR_REVIEW' || u.status === 'CONFIRMED'}
                  onClick={() => this.handleReviewExisting(u)}
                >
                  <UploadFileName>{u.fileName}</UploadFileName>
                  <StatusBadge status={u.status}>
                    {u.status === 'READY_FOR_REVIEW'
                      ? 'Ready for Review'
                      : u.status.replace(/_/g, ' ')}
                  </StatusBadge>
                </UploadRow>
              ))}
            </UploadsList>
          );
        }}
      />
    );
  }

  render() {
    const { activeUpload } = this.state;

    return (
      <Page>
        <Header>
          <Title>{i18n.t('Upload Customer PO')}</Title>
          <BackLink onClick={() => this.props.history.push('/orders')}>
            {i18n.t('Back to Orders')}
          </BackLink>
        </Header>

        {activeUpload ? this.renderActiveUpload() : this.renderUploadArea()}
        {!activeUpload && this.renderPreviousUploads()}
      </Page>
    );
  }
}

export default POUploadScene;
